import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity, UserPlus, ArrowRight, MessageSquare, UsersRound, Send, ShieldCheck,
  GraduationCap, BookOpen, Droplet, CheckCircle2, Inbox,
} from "lucide-react";
import { api } from "../lib/api";
import { stageLabel, leadStatusLabel } from "../lib/constants";

interface ActivityRow {
  id: string;
  entity_type: string;
  entity_id: string;
  action: string;
  details: string | null;
  user_name: string | null;
  entity_name: string | null;
  created_at: number;
}

function timeAgo(ts: number): string {
  const seconds = Math.floor(Date.now() / 1000 - ts);
  if (seconds < 60) return "just now";
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(ts * 1000).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

function describe(row: ActivityRow): { icon: any; text: any; tint: string } {
  const who = row.user_name ?? "Someone";
  const name = row.entity_name ?? "—";
  let details: any = {};
  try { if (row.details) details = JSON.parse(row.details); } catch {}

  switch (`${row.entity_type}:${row.action}`) {
    case "person:created":
      return {
        icon: UserPlus, tint: "text-blue-700 bg-blue-100",
        text: <><strong>{who}</strong> added <strong>{name}</strong></>,
      };
    case "person:stage_changed":
      return {
        icon: ArrowRight, tint: "text-amber-700 bg-amber-100",
        text: <><strong>{who}</strong> moved <strong>{name}</strong> from <em>{stageLabel(details.from)}</em> to <em>{stageLabel(details.to)}</em></>,
      };
    case "person:group_changed":
      return {
        icon: UsersRound, tint: "text-purple-700 bg-purple-100",
        text: <><strong>{who}</strong> updated group for <strong>{name}</strong></>,
      };
    case "person:note_added":
      return {
        icon: MessageSquare, tint: "text-green-700 bg-green-100",
        text: <><strong>{who}</strong> added a note on <strong>{name}</strong></>,
      };
    case "person:baptised":
      return {
        icon: Droplet, tint: "text-sky-700 bg-sky-100",
        text: <><strong>{who}</strong> recorded baptism for <strong>{name}</strong></>,
      };
    case "lead:created":
      return {
        icon: Inbox, tint: "text-pink-700 bg-pink-100",
        text: <><strong>{who}</strong> received lead <strong>{name}</strong></>,
      };
    case "lead:assigned":
      return {
        icon: ArrowRight, tint: "text-indigo-700 bg-indigo-100",
        text: <><strong>{who}</strong> assigned <strong>{name}</strong> to <em>{details.leader ?? "a leader"}</em></>,
      };
    case "lead:unassigned":
      return {
        icon: ArrowRight, tint: "text-ink-600 bg-ink-100",
        text: <><strong>{who}</strong> unassigned <strong>{name}</strong></>,
      };
    case "lead:status_changed":
      return {
        icon: ArrowRight, tint: "text-amber-700 bg-amber-100",
        text: <><strong>{who}</strong> moved lead <strong>{name}</strong> to <em>{leadStatusLabel(details.to)}</em></>,
      };
    case "lead:completion_outcome":
      return {
        icon: CheckCircle2, tint: "text-green-700 bg-green-100",
        text: <><strong>{who}</strong> set outcome for <strong>{name}</strong>: <em>{details.outcome === "multiplying" ? "Disciple Ready to Make Disciples" : "Ongoing Discipleship"}</em></>,
      };
    case "lead:converted":
      return {
        icon: UserPlus, tint: "text-green-700 bg-green-100",
        text: <><strong>{who}</strong> converted lead <strong>{name}</strong> to a person</>,
      };
    case "leader:approved":
      return {
        icon: ShieldCheck, tint: "text-blue-700 bg-blue-100",
        text: <><strong>{who}</strong> approved <strong>{name}</strong> as a leader</>,
      };
    case "leader:training_confirmed":
      return {
        icon: GraduationCap, tint: "text-green-700 bg-green-100",
        text: <><strong>{who}</strong> confirmed training for <strong>{name}</strong> — now Active</>,
      };
    case "group:created":
      return {
        icon: UsersRound, tint: "text-purple-700 bg-purple-100",
        text: <><strong>{who}</strong> created group <strong>{name}</strong></>,
      };
    case "session:recorded":
      return {
        icon: BookOpen, tint: "text-brand-700 bg-brand-50",
        text: <><strong>{who}</strong> recorded a DBS session for <strong>{details.group_name ?? name}</strong>{details.attendees ? ` · ${details.attendees} attendees` : ""}</>,
      };
    default:
      return {
        icon: Activity, tint: "text-ink-600 bg-ink-100",
        text: <><strong>{who}</strong> {row.action.replace(/_/g, " ")} {name && <strong>{name}</strong>}</>,
      };
  }
}

export function ActivityFeed({ limit = 8 }: { limit?: number }) {
  const [rows, setRows] = useState<ActivityRow[] | null>(null);

  useEffect(() => {
    api.get<{ activity: ActivityRow[] }>("/dashboard/activity")
      .then((r) => setRows(r.activity))
      .catch(() => setRows([]));
  }, []);

  if (rows === null) return <div className="text-sm text-ink-500">Loading…</div>;
  if (rows.length === 0) {
    return <div className="text-sm text-ink-500">No activity yet — events show up here as you add and update people, leads, and groups.</div>;
  }

  return (
    <div className="space-y-3">
      {rows.slice(0, limit).map((r) => {
        const d = describe(r);
        const Icon = d.icon;
        let link: string | null = null;
        if (r.entity_type === "lead") link = "/leads";
        else if (r.entity_type === "person") link = `/people/${r.entity_id}`;
        else if (r.entity_type === "leader") link = "/leaders";
        else if (r.entity_type === "group") link = `/groups/${r.entity_id}`;
        else if (r.entity_type === "session") {
          let det: any = {};
          try { if (r.details) det = JSON.parse(r.details); } catch {}
          link = det.group_id ? `/groups/${det.group_id}` : null;
        }
        const Wrapper: any = link ? Link : "div";
        return (
          <Wrapper
            key={r.id}
            {...(link ? { to: link } : {})}
            className={`flex items-start gap-3 ${link ? "hover:bg-ink-50 -mx-2 px-2 py-1.5 rounded-md transition" : ""}`}
          >
            <span className={`shrink-0 h-8 w-8 rounded-full grid place-items-center ${d.tint}`}>
              <Icon className="h-4 w-4" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-ink-700">{d.text}</div>
              <div className="text-xs text-ink-400 mt-0.5">{timeAgo(r.created_at)}</div>
            </div>
          </Wrapper>
        );
      })}
    </div>
  );
}
